import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import chipCloseWhite from "assets/images/loading/chip-close-white.svg";
import chipPlusBlue from "assets/images/loading/chip-plus-blue.svg";
import containerStorageIcon from "assets/images/loading/container-storage.svg";
import truckIcon from "assets/images/loading/truck-icon.svg";
import cn from "classnames";
import { TruckAxis } from "components/Local/Transport/TruckAxis/TruckAxis";
import { Button, BUTTON_VARIANT } from "components/UI/Button";
import { CustomCheckbox } from "components/UI/CustomCheckbox";
import { CustomSelect } from "components/UI/CustomSelect";
import { OptionType } from "components/UI/CustomSelect/CustomSelect.types";
import { ArrowDown, ArrowUp, Plus, Trash } from "components/UI/Icons";
import { Input } from "components/UI/Input";
import { useOnClickOutside } from "hooks/useOnClickOutside.hook";
import { useTranslation } from "localization/localization";
import { RootState } from "store";
import { AppDOM } from "utils/app.utils";
import { containers } from "utils/containers";
import { loading_rules } from "utils/data";
import { getImageSrc } from "utils/images";
import { trucks } from "utils/trucks";
import { TransportUnitCardProps } from "./TransportUnitCard.types";
import s from "./TransportUnitCard.module.scss";

const unitOptionIcon = (src: string) => (
  <span className={s.unitIcon}>
    <img src={src} alt="" />
  </span>
);

const UNIT_OPTIONS: OptionType[] = [
  {
    value: "container",
    label: "Container",
    icon: unitOptionIcon(containerStorageIcon),
    valueIcon: unitOptionIcon(containerStorageIcon),
  },
  {
    value: "truck",
    label: "Truck",
    icon: unitOptionIcon(truckIcon),
    valueIcon: unitOptionIcon(truckIcon),
  },
];

const containerOptions: OptionType[] = containers
  .filter((item) => !item.disabled)
  .map((item) => ({ value: item.type, label: item.name }));

const truckOptions: OptionType[] = trucks
  .filter((item) => !item.disabled)
  .map((item) => ({ value: item.type, label: item.name }));

const loadingRuleOptions: OptionType[] = loading_rules.map((rule: { name: string; value: string }) => ({
  value: rule.value,
  label: rule.name,
}));

export const TransportUnitCard = ({
  item,
  groups,
  showMoveButtons = false,
  removeTransport,
  changeTransportIndex,
  changeTransport,
  changeContainerCount,
  changeLoadingRules,
  setAllowGroups,
  changeAllowGroups: _changeAllowGroups,
}: TransportUnitCardProps) => {
  const { t } = useTranslation();
  const { lengthUnits, weightUnits } = useSelector((state: RootState) => state.root.options);
  const autoCountContainers = useSelector((state: RootState) => state.root.autoCountContainers);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside({
    ref: pickerRef,
    shadowRoot: AppDOM.shadowRootNode,
    handler: () => setPickerOpen(false),
  });

  const isContainer = item.type === "container";
  const typeOptions = isContainer ? containerOptions : truckOptions;
  const selectedUnit = UNIT_OPTIONS.find((option) => option.value === item.type) ?? UNIT_OPTIONS[0];
  const selectedType = typeOptions.find((option) => option.value === item.attr.type) ??
    typeOptions[0] ?? { value: "", label: "" };
  const selectedRule = loadingRuleOptions.find((option) => option.value === item.loadType) ?? loadingRuleOptions[0];

  const isCustom = String(item.attr.type).includes("custom");
  const qty = item.qty ?? 1;
  const allowGroups: number[] | null = item.allowGroups ?? null;
  const selectedGroupIndexes = allowGroups === null ? [] : allowGroups;

  const imageSrc = isContainer
    ? getImageSrc(item.attr.type === "custom" ? "20st" : item.attr.type)
    : getImageSrc(String(item.attr.type).includes("custom") ? "tautliner" : item.attr.type);

  const handleUnitChange = (option: OptionType) => {
    if (option.value === item.type) return;
    changeTransport(item.uid, option.value === "truck" ? "tautliner" : "20st");
  };

  const handleTypeChange = (option: OptionType) => {
    changeTransport(item.uid, option.value);
  };

  const handleRuleChange = (option: OptionType) => {
    changeLoadingRules(item.uid, option.value);
  };

  const handleQty = (next: number) => {
    if (next < 1 || autoCountContainers.length) return;
    changeContainerCount(item.uid, next);
  };

  const handleAddGroupClick = () => {
    setPickerOpen((open) => !open);
  };

  const handleToggleGroup = (index: number) => {
    setAllowGroups(index, item.uid);
  };

  const isGroupSelected = (index: number) => {
    if (allowGroups === null) return false;
    return allowGroups.includes(index);
  };

  return (
    <article className={s.card}>
      <div className={s.header}>
        <div className={s.selectField}>
          <span className={s.label}>{t("Transport unit")}</span>
          <CustomSelect
            options={UNIT_OPTIONS}
            value={selectedUnit}
            handleChange={handleUnitChange}
            isSearchable={false}
            selectedIndicator="checkmark"
            componentsClassNames={{ menu: s.unitMenu, option: s.unitOption }}
          />
        </div>

        <div className={s.selectField}>
          <span className={s.label}>{isContainer ? t("Container type") : t("Truck type")}</span>
          <CustomSelect
            options={typeOptions}
            value={selectedType}
            handleChange={handleTypeChange}
            isSearchable={false}
            selectedIndicator="checkmark"
          />
        </div>

        <div className={s.actions}>
          {showMoveButtons && (
            <>
              <Button
                type="button"
                variant={BUTTON_VARIANT.ONLY_ICON}
                aria-label={t("Move down")}
                onClick={() => changeTransportIndex(item.uid, "down")}
              >
                <ArrowDown />
              </Button>
              <Button
                type="button"
                variant={BUTTON_VARIANT.ONLY_ICON}
                aria-label={t("Move up")}
                onClick={() => changeTransportIndex(item.uid, "up")}
              >
                <ArrowUp />
              </Button>
            </>
          )}
          <Button
            type="button"
            variant={BUTTON_VARIANT.ONLY_ICON}
            aria-label={t("Delete")}
            onClick={() => removeTransport(item.uid)}
          >
            <Trash />
          </Button>
        </div>
      </div>

      <div className={s.body}>
        <div className={s.image}>{imageSrc ? <img src={imageSrc} alt="" /> : null}</div>

        <div className={s.fields}>
          <div className={s.row}>
            <Input
              label={`${t("Length")}, ${lengthUnits}`}
              type="number"
              value={item.spaces[0].length}
              disabled={!isCustom}
              name={`length-${item.uid}`}
              readOnly={!isCustom}
            />
            <Input
              label={`${t("Width")}, ${lengthUnits}`}
              type="number"
              value={item.spaces[0].width}
              disabled={!isCustom}
              name={`width-${item.uid}`}
              readOnly={!isCustom}
            />
            <Input
              label={`${t("Height")}, ${lengthUnits}`}
              type="number"
              value={item.spaces[0].height}
              disabled={!isCustom}
              name={`height-${item.uid}`}
              readOnly={!isCustom}
            />
            <Input
              label={`${t("Max weight")}, ${weightUnits}`}
              type="number"
              value={item.spaces[0].maxWeight}
              disabled={!isCustom}
              name={`maxMass-${item.uid}`}
              readOnly={!isCustom}
            />
          </div>

          <div className={s.row}>
            <div className={s.counterField}>
              <span className={s.label}>{t("Count")}</span>
              <div className={s.counter}>
                <button
                  type="button"
                  className={s.counterBtn}
                  aria-label={t("Decrease")}
                  disabled={!!autoCountContainers.length || qty <= 1}
                  onClick={() => handleQty(qty - 1)}
                >
                  −
                </button>
                <span className={s.counterValue}>{qty}</span>
                <button
                  type="button"
                  className={s.counterBtn}
                  aria-label={t("Increase")}
                  disabled={!!autoCountContainers.length}
                  onClick={() => handleQty(qty + 1)}
                >
                  <Plus color="#000C37" />
                </button>
              </div>
            </div>

            <div className={s.selectFieldNarrow}>
              <span className={s.label}>{t("Loading rules")}</span>
              <CustomSelect
                options={loadingRuleOptions}
                value={selectedRule}
                handleChange={handleRuleChange}
                isSearchable={false}
                selectedIndicator="none"
              />
            </div>
          </div>
        </div>

        {!isContainer && <TruckAxis item={item} lengthUnits={lengthUnits} weightUnits={weightUnits} />}
      </div>

      <div className={s.groups}>
        <span className={s.groupsTitle}>{t("Groups")}</span>
        <div className={s.groupList}>
          {selectedGroupIndexes.map((index) => {
            const group = groups[index];
            if (!group) return null;
            return (
              <button
                key={`${item.uid}-group-${index}`}
                type="button"
                className={s.groupChip}
                aria-label={`${t("Remove")} ${group.name}`}
                onClick={() => setAllowGroups(index, item.uid)}
              >
                <img src={chipCloseWhite} alt="" width={16} height={16} className={s.chipIcon} />
                <span>{group.name}</span>
              </button>
            );
          })}

          <div className={s.addGroupWrap} ref={pickerRef}>
            <button type="button" className={s.addGroup} onClick={handleAddGroupClick} aria-expanded={pickerOpen}>
              <img src={chipPlusBlue} alt="" width={16} height={16} className={s.chipIcon} />
              <span>{t("Add group")}</span>
            </button>

            {pickerOpen && (
              <div className={s.groupPicker} role="listbox" aria-multiselectable="true" aria-label={t("Groups")}>
                <ul className={s.groupPickerList}>
                  {groups.map((group: { name?: string }, index: number) => {
                    const selected = isGroupSelected(index);
                    return (
                      <li key={`${item.uid}-pick-${index}`}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={cn(s.groupPickerItem, { [s.groupPickerItemActive]: selected })}
                          onClick={() => handleToggleGroup(index)}
                        >
                          <span className={s.groupPickerLabel}>{group.name}</span>
                          <span className={s.checkboxReadonly}>
                            <CustomCheckbox checked={selected} />
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};
